import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getNotifications, getUnreadCount, markAllRead, markOneRead, supabase } from '../lib/supabase';

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return Math.floor(diff / 86400) + 'd ago';
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    loadUnread();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadUnread, 30000);

    // Real-time subscription
    const channel = supabase
      .channel('notifications_' + user.id)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: 'user_id=eq.' + user.id,
      }, () => { loadUnread(); })
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [user]); // eslint-disable-line

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadUnread = async () => {
    if (!user) return;
    try { setUnread(await getUnreadCount(user.id)); }
    catch (e) {}
  };

  const handleOpen = async () => {
    if (!user) return;
    const wasOpen = open;
    setOpen(o => !o);
    if (!wasOpen) {
      try {
        const data = await getNotifications(user.id);
        setNotifications(data || []);
      } catch (e) {}
    }
  };

  const handleClickNotif = async (notif) => {
    try { await markOneRead(notif.id); }
    catch (e) {}
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    setUnread(prev => Math.max(0, prev - (notif.is_read ? 0 : 1)));
    setOpen(false);
    if (notif.listing_id) navigate('/listing/' + notif.listing_id);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (e) {}
  };

  if (!user) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        style={{
          background: 'transparent',
          border: '1px solid rgba(255,255,255,.18)',
          borderRadius: 'var(--r)',
          padding: '.38rem .7rem',
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          color: 'var(--cream)',
          fontSize: '1rem',
          transition: 'border-color .18s',
          minHeight: 36,
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--terracotta)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,.18)'}
      >
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute',
            top: -6, right: -6,
            background: 'var(--terracotta)',
            color: '#fff',
            fontSize: '.65rem',
            fontWeight: 700,
            width: 18, height: 18,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid var(--ink)',
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: 320,
          background: 'white',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          boxShadow: '0 8px 32px rgba(0,0,0,.18)',
          zIndex: 300,
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.75rem 1rem', borderBottom: '1px solid var(--border)', background: 'var(--cream)' }}>
            <span style={{ fontFamily: 'var(--font-d)', fontWeight: 700, fontSize: '.95rem' }}>Notifications</span>
            {unread > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '.75rem', color: 'var(--terracotta)', fontWeight: 600, padding: 0 }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--brown)', fontSize: '.875rem' }}>
                <div style={{ fontSize: '1.75rem', marginBottom: '.5rem' }}>🔔</div>
                No notifications yet
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleClickNotif(n)}
                  style={{
                    padding: '.75rem 1rem',
                    borderBottom: '1px solid var(--border)',
                    cursor: n.listing_id ? 'pointer' : 'default',
                    background: n.is_read ? 'white' : '#FFF8F6',
                    display: 'flex',
                    gap: '.75rem',
                    alignItems: 'flex-start',
                    transition: 'background .15s',
                  }}
                  onMouseEnter={e => { if (n.listing_id) e.currentTarget.style.background = '#FFF2EF'; }}
                  onMouseLeave={e => e.currentTarget.style.background = n.is_read ? 'white' : '#FFF8F6'}
                >
                  {/* Unread dot */}
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.is_read ? 'transparent' : 'var(--terracotta)', flexShrink: 0, marginTop: 6 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.875rem', color: 'var(--ink)', lineHeight: 1.4 }}>{n.message}</div>
                    <div style={{ fontSize: '.75rem', color: 'var(--brown)', marginTop: '.2rem' }}>{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
